/**
 * CreateOrgDialog — org-name prompt for "Create new organization" (VMS-72).
 *
 * Opened from the avatar dropdown (a DropdownMenu can't host a text input).
 * On success: POST /setup via api.createOrg, enter the new org (which clears
 * the outgoing org's client-side state — see auth/orgScope.ts), then hard-
 * navigate to `/` so every org-scoped hook refetches under the new org.
 */

import React, { useState } from 'react';
import { toast } from 'sonner';

import { api } from '@/api';
import { Hint } from '@/components/occurra/Hint';
import { Input } from '@/components/occurra/Input';
import { Button } from '@/components/shadcn/button';
import {
    Dialog, DialogContent, DialogFooter,
    DialogHeader, DialogTitle,
} from '@/components/shadcn/dialog';

import { authSession } from './authSession';
import { reloadIntoOrg } from './orgScope';

interface Props {
    open: boolean;
    onOpenChange: (isOpen: boolean) => void;
}

function createBlockedReason(isBusy: boolean, name: string): string | undefined {
    if (isBusy) {
        return 'Creating the organization';
    }
    return name ? undefined : 'Enter an organization name';
}

export const CreateOrgDialog = (
    { open: isOpen, onOpenChange }: Props,
): React.JSX.Element => {
    const [orgName, setOrgName] = useState('');
    const [isBusy, setBusy] = useState(false);
    const blockedReason = createBlockedReason(isBusy, orgName.trim());

    async function onSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
        e.preventDefault();
        const name = orgName.trim();
        if (!name) {
            return;
        }
        setBusy(true);
        try {
            const me = await api.createOrg(name);
            // The response carries the new organization's session; the cookie
            // was re-set alongside.
            if (me.token) {
                authSession.setToken(me.token);
            }
            reloadIntoOrg(me.orgId ?? null);
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Could not create organization');
            setBusy(false);
        }
    }

    return (
        <Dialog
            open={isOpen}
            onOpenChange={(v) => { if (!isBusy) { setOrgName(''); onOpenChange(v); } }}
        >
            <DialogContent className="theme-occurra sm:max-w-sm">
                <DialogHeader>
                    <DialogTitle>Create new organization</DialogTitle>
                </DialogHeader>
                <form
                    className="flex flex-col gap-3"
                    onSubmit={onSubmit}
                >
                    <Input
                        autoFocus
                        required
                        aria-label="Organization name"
                        placeholder="My Company"
                        value={orgName}
                        onChange={(e) => setOrgName(e.target.value)}
                    />
                    <DialogFooter>
                        <Hint label={blockedReason}>
                            <Button
                                type="submit"
                                disabled={blockedReason !== undefined}
                            >
                                {isBusy ? 'Creating…' : 'Create and switch'}
                            </Button>
                        </Hint>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
